import { useCallback, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceArea,
    ComposedChart,
    Scatter,
    Area,
} from "recharts";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { calculateYBands } from "@/lib/utils/sensitivity";
import { Skeleton } from "@heroui/react";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import { useFormContext } from "react-hook-form";
import useRequest from "@/lib/request/useRequest";
import { roundTo } from "@/lib/utils/numbers";
import { COLOR_MAP } from "@/types/color";
import { Field } from "@/types/field";
import { FillLayerSpecification, SymbolLayerSpecification } from "mapbox-gl";
import { LineLayerSpecification } from "mapbox-gl";
import useMap from "@/lib/map/useMap";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeSourcesIfExists,
    removeLayersIfExists,
    moveLayersToTop,
} from "@/lib/utils/map";
import { Plot, TrendlineTable } from "@/types/plot";
import { FeatureCollection, MultiPolygon } from "geojson";
import { UseQueryResult } from "@tanstack/react-query";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import { scaleTime } from "d3-scale";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";

const GRAPH_SOURCE = "trendline-graph-source";
const FILL_LAYER: FillLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "trendline-graph-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    layout: {},
    paint: {
        "fill-color": ["get", "statusColor"],
        "fill-opacity": 0.8,
    },
};
const LINE_LAYER: LineLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "trendline-graph-line",
    type: "line",
    layout: {},
    paint: {
        "line-color": ["get", "displayColor"],
        "line-width": 3,
    },
    minzoom: 14,
};
const SYMBOL_LAYER: SymbolLayerSpecification = {
    source: GRAPH_SOURCE,
    id: "trendline-graph-symbol",
    type: "symbol",
    layout: {
        "text-field": [
            "concat",
            ["case", ["==", ["get", "plot_type"], 1], "Can: ", "Ref: "],
            ["get", "pair_id"],
        ],
        "text-size": 12,
        "symbol-avoid-edges": true,
    },
};

function SITrendline({
    fieldId,
    senstiivity,
    disableResize,
    tableQuery,
}: {
    fieldId: string;
    senstiivity?: Field["insight_sensitivity"];
    disableResize?: boolean;
    tableQuery?: UseQueryResult<TrendlineTable[]>;
}) {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("insights");
    const { useGetRequest } = useRequest();
    const f = useFormContext();
    const { bulkSIEnabled, virtualSIEnabled } = useFeatureFlag();
    const [
        startDate,
        endDate,
        pairIds,
        rangeOnTrendline,
        showBulkSI,
        showVirtualSI,
    ] = f.watch([
        "startDate",
        "endDate",
        "pairIds",
        "rangeOnTrendline",
        "showBulkSI",
        "showVirtualSI",
    ]);

    const trendlineQuery = useGetRequest<(number | string)[][]>({
        url: `fields/${fieldId}/trendline/`,
        enabled: !!fieldId,
        params: {
            values: "si",
            groupby: "block",
            range: true,
        },
    });

    const bulkQuery = useGetRequest<
        {
            image_id: number;
            capture_date: string;
            mean_sensitized_si: number;
        }[]
    >({
        url: `fields/${fieldId}/bulk_trendline/`,
        enabled: !!fieldId && bulkSIEnabled,
    });

    const virtualSIQuery = useGetRequest<
        {
            imagery__capture_date__date: number;
            min_si: number;
            mean_si: number;
            max_si: number;
        }[]
    >({
        url: `fields/${fieldId}/virtual_trendline/`,
        enabled: !!fieldId,
    });

    const uploadedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/uploaded_plots/`,
        params: { boundary: "analytic" },
    });
    const appliedPlots = useGetRequest<FeatureCollection<MultiPolygon, Plot>>({
        url: `fields/${fieldId}/applied_plots/`,
        params: { boundary: "analytic" },
    });
    const plots = useMemo(() => {
        return [
            ...(uploadedPlots?.data?.features ?? []),
            ...(appliedPlots?.data?.features ?? []),
        ];
    }, [appliedPlots?.data?.features, uploadedPlots?.data?.features]);

    const trendlineInCycle = useMemo(() => {
        if (!trendlineQuery.data) return [];

        const keys = trendlineQuery.data[0];

        return sortArrayByKey(
            trendlineQuery.data
                .slice(1)
                .map((row) => {
                    return row.reduce(
                        (obj, value, index) => {
                            if (value === null || value === 0) return obj;
                            const isCaptureDate = keys[index] == "capture_date";
                            obj[keys[index].toString()] = isCaptureDate
                                ? dayjs(value).toDate().getTime()
                                : roundTo((value as number) * 100);
                            return obj;
                        },
                        {} as {
                            capture_date: string;
                            [x: string | number]: number | string | Date | null;
                        },
                    );
                })
                .filter((row) => {
                    return (
                        dayjs(row.capture_date).isSameOrAfter(
                            dayjs(startDate),
                        ) &&
                        dayjs(row.capture_date).isSameOrBefore(dayjs(endDate))
                    );
                })
                .map((row) => {
                    if (!pairIds?.length) return row;
                    return Object.fromEntries(
                        Object.entries(row).filter(
                            ([key]) =>
                                key === "capture_date" ||
                                pairIds.includes(
                                    Number(key.split("_")[1]).toString(),
                                ),
                        ),
                    );
                }),
            "capture_date",
        );
    }, [endDate, pairIds, startDate, trendlineQuery.data]);

    const dataKeys = useMemo(() => {
        if (!trendlineInCycle?.length) return [];
        const pairIds = Array.from(
            new Set(
                trendlineInCycle
                    .map((row) => Object.keys(row))
                    .flat()
                    .map((key) => key.split("_")[1]),
            ),
        );
        return pairIds.slice(1).map((pair, index) => {
            return {
                pairId: pair,
                color: COLOR_MAP[index],
            };
        });
    }, [trendlineInCycle]);

    const [min, max] = useMemo(() => {
        const values = (trendlineInCycle
            .map((point) => Object.values(point).slice(1))
            .flat()
            .filter((val) => val !== null && val != 0) ?? []) as number[];
        return [
            Math.max(Math.floor(Math.min(...values) / 5) * 5, 0),
            Math.min(Math.ceil(Math.max(...values) / 5) * 5, 110),
        ];
    }, [trendlineInCycle]);
    const bands = useMemo(
        () => calculateYBands(senstiivity ?? "moderate"),
        [senstiivity],
    );

    const { elementRef, isResizing } = useResizeObserver(250);

    const { map, mapLoaded } = useMap();

    useEffect(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, GRAPH_SOURCE, {
            type: "geojson",
            tolerance: 0,
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
        addUpdateGeojsonLayers(map, [FILL_LAYER, LINE_LAYER, SYMBOL_LAYER]);

        return () => {
            if (map) {
                removeLayersIfExists(map, [
                    FILL_LAYER.id,
                    LINE_LAYER.id,
                    SYMBOL_LAYER.id,
                ]);
                removeSourcesIfExists(map, [GRAPH_SOURCE]);
            }
        };
    }, [map, mapLoaded]);

    const plotIdsByDate = useMemo(() => {
        return tableQuery?.data?.reduce(
            (acc, row) => {
                const captureDate = dayjs(row.capture_date).format(
                    "YYYY-MM-DD",
                );
                if (!acc[captureDate]) {
                    acc[captureDate] = [row.plot_id];
                }
                acc[captureDate].push(row.plot_id);
                return acc;
            },
            {} as Record<string, number[]>,
        );
    }, [tableQuery?.data]);

    const onEnter: CategoricalChartFunc = useCallback(
        (data) => {
            if (!map || !mapLoaded) return;

            const activeDate = data.activePayload?.[0]?.payload?.capture_date;
            if (!activeDate) return;

            const plotIds =
                plotIdsByDate?.[dayjs(activeDate).format("YYYY-MM-DD")];
            if (!plotIds) return;
            const features =
                plots
                    ?.filter((plot) => plotIds?.includes(plot.id as number))
                    .map((plot) => {
                        const pairId = plot.properties.pair_id;
                        const displayColor = dataKeys?.find(
                            (d) => Number(d.pairId) === pairId,
                        )?.color;
                        const payload = data.activePayload?.[0]?.payload;
                        const statusColor = payload
                            ? bands?.find((band) => {
                                  return (
                                      payload[`mean_${pairId}`] >= band.y0 &&
                                      payload[`mean_${pairId}`] < band.y1
                                  );
                              })?.mapColor
                            : "gray";
                        return {
                            ...plot,
                            properties: {
                                ...plot.properties,
                                displayColor: displayColor || "gray",
                                statusColor: statusColor || "gray",
                            },
                        };
                    }) ?? [];

            addUpdateGeojsonSource(map, GRAPH_SOURCE, {
                type: "geojson",
                tolerance: 0,
                data: {
                    type: "FeatureCollection",
                    features,
                },
            });
            addUpdateGeojsonLayers(map, [SYMBOL_LAYER]);
            moveLayersToTop(map, [
                FILL_LAYER.id,
                LINE_LAYER.id,
                SYMBOL_LAYER.id,
            ]);
        },
        [map, mapLoaded, plotIdsByDate, plots, dataKeys, bands],
    );

    const onLeave = useCallback(() => {
        if (!map || !mapLoaded) return;

        addUpdateGeojsonSource(map, GRAPH_SOURCE, {
            type: "geojson",
            tolerance: 0,
            data: {
                type: "FeatureCollection",
                features: [],
            },
        });
        removeLayersIfExists(map, [SYMBOL_LAYER.id]);
    }, [map, mapLoaded]);

    const maxValue = Math.max(
        ...trendlineInCycle.map((d) => d.capture_date as number),
    );
    const minValue = Math.min(
        ...trendlineInCycle.map((d) => d.capture_date as number),
    );
    const timeScale = scaleTime().domain([minValue, maxValue]).nice(5);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const operations = useGetRequest<{
        operations: { id: number; average_N: number; start_date: string }[];
    }>({
        url: `fields/${fieldId}/application_operations/`,
    });
    const operationsData = useMemo(() => {
        return (
            operations.data?.operations?.reduce(
                (acc, operation) => {
                    const startDate = dayjs(operation.start_date)
                        .toDate()
                        .getTime();
                    const totalN = roundTo(operation.average_N, 1);
                    if (!acc[startDate]) {
                        acc[startDate] = {
                            start_date: startDate,
                            total_N: totalN,
                        };
                    }
                    acc[startDate].total_N += totalN;
                    return acc;
                },
                {} as {
                    [x: string]: { start_date: number; total_N: number };
                },
            ) ?? {}
        );
    }, [operations.data?.operations]);

    const chartData = useMemo(() => {
        const trendlineDates = trendlineInCycle.map((point) =>
            dayjs(point.capture_date as number)
                .toDate()
                .getTime(),
        );
        const operationDates =
            operations.data?.operations
                ?.filter(
                    (op) =>
                        dayjs(op.start_date).isSameOrAfter(dayjs(startDate)) &&
                        dayjs(op.start_date).isSameOrBefore(dayjs(endDate)),
                )
                ?.map((op) => dayjs(op.start_date).toDate().getTime()) ?? [];
        const allDatesSet = new Set([...trendlineDates, ...operationDates]);
        const allDates = Array.from(allDatesSet).sort((a, b) => a - b);

        return allDates.map((date) => {
            const trendlinePoint = trendlineInCycle.find(
                (point) =>
                    dayjs(point.capture_date as number)
                        .toDate()
                        .getTime() === date,
            );
            const operation = operationsData?.[date];

            const bulk_si = bulkQuery.data?.find(
                (point) =>
                    dayjs(point.capture_date).toDate().getTime() === date,
            )?.mean_sensitized_si;

            const virtual_si = virtualSIQuery.data?.find(
                (point) =>
                    dayjs(point.imagery__capture_date__date)
                        .toDate()
                        .getTime() === date,
            );

            const chartPoint = {
                ...(trendlinePoint ?? { capture_date: date }),
                total_N: operation ? min + 1 : null,
                bulk_si: bulk_si ? bulk_si * 100 : null,

                virtual_min_si: virtual_si?.min_si
                    ? roundTo(virtual_si?.min_si ?? null, 1)
                    : null,
                virtual_mean_si: virtual_si?.mean_si
                    ? roundTo(virtual_si?.mean_si ?? null, 1)
                    : null,
                virtual_max_si: virtual_si?.max_si
                    ? roundTo(virtual_si?.max_si ?? null, 1)
                    : null,
                virtual_range: [virtual_si?.min_si, virtual_si?.max_si],
            } as {
                capture_date: number;
                total_N: number | null;
                bulk_si: number | null;
                virtual_min_si: number | null;
                virtual_mean_si: number | null;
                virtual_max_si: number | null;
                virtual_range: number[] | null;
                [key: string]: number | number[] | null;
            };
            const pairIds = dataKeys?.map((d) => d.pairId);
            if (trendlinePoint) {
                pairIds?.forEach((pairId) => {
                    chartPoint[`range_${pairId}`] = [
                        trendlinePoint[`min_${pairId}`] as number,
                        trendlinePoint[`max_${pairId}`] as number,
                    ];
                });
            }
            return chartPoint;
        });
    }, [
        trendlineInCycle,
        operations.data?.operations,
        startDate,
        endDate,
        operationsData,
        bulkQuery.data,
        virtualSIQuery.data,
        min,
        dataKeys,
    ]);

    const operationsByDate = useMemo(() => {
        if (!operations.data?.operations) return {};
        return operations.data.operations.reduce(
            (acc, op) => {
                const date = dayjs(op.start_date).toDate().getTime();
                acc[date] = roundTo(op.average_N, 1);
                return acc;
            },
            {} as Record<number, number>,
        );
    }, [operations.data?.operations]);

    return (
        <div className="w-full h-[28rem]" ref={elementRef}>
            {isResizing && !disableResize && (
                <Skeleton className="h-full w-full" />
            )}
            {(!isResizing || disableResize) && (
                <ResponsiveContainer width="100%" height={400}>
                    {!trendlineInCycle.length ? (
                        <div className="bg-gray-100 h-full rounded-sm">
                            <div className=" p-3 italic text-center">
                                {t("messages.noDataSelectedDates")}
                            </div>
                        </div>
                    ) : (
                        <ComposedChart
                            data={chartData}
                            margin={{ bottom: 16, left: 8, right: 4 }}
                            onMouseMove={onEnter}
                            onMouseLeave={onLeave}
                        >
                            <CartesianGrid />
                            <XAxis
                                dataKey="capture_date"
                                spacing={50}
                                dy={16}
                                dx={-8}
                                angle={-10}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                scale="linear"
                                type="number"
                                domain={domain}
                                ticks={ticks}
                            />
                            <YAxis
                                domain={[Number(min), Number(max)]}
                                allowDataOverflow
                                allowDecimals={false}
                                tickCount={(max - min) / 5 + 1}
                                tickFormatter={(t) => `${t}%`}
                                tickSize={0}
                                fontSize={15}
                                label={{
                                    value: t("chartLabels.sufficiency"),
                                    angle: -90,
                                    position: "left",
                                    dx: 10,
                                }}
                            />
                            <Tooltip
                                cursor={{ stroke: "#000", strokeWidth: 2 }}
                                labelFormatter={(l) => dayjs(l).format("ll")}
                                formatter={(value, name, payload) => {
                                    if (name === "total_N") {
                                        const appOp =
                                            operationsByDate[
                                                payload.payload.capture_date
                                            ];
                                        const lbsN = appOp ?? "--";
                                        return [
                                            `${lbsN} lbs N`,
                                            t("tooltips.nApplied"),
                                        ];
                                    }
                                    if (name === "virtual_mean_si") {
                                        return [`${value}%`, t("virtualSI")];
                                    }
                                    if (name.toString().includes("mean_")) {
                                        const plotId = name
                                            .toString()
                                            .replace("mean_", "");
                                        return [
                                            `${value}%`,
                                            `${t("chartLabels.pairIdHash")}${plotId}`,
                                        ];
                                    }

                                    if (name.toString().includes("range_")) {
                                        return [undefined, undefined];
                                    }
                                }}
                            />

                            {bands?.map((band) => {
                                return (
                                    <ReferenceArea
                                        key={band.color}
                                        y1={band.y0}
                                        y2={band.y1}
                                        fill={band.color}
                                        ifOverflow="hidden"
                                        fillOpacity={0.6}
                                    />
                                );
                            })}
                            {rangeOnTrendline &&
                                dataKeys?.map((key) => {
                                    return (
                                        <Area
                                            type="monotone"
                                            key={`range_${key.pairId}`}
                                            dataKey={`range_${key.pairId}`}
                                            fillOpacity={0.3}
                                            stroke="none"
                                            fill={key.color}
                                            connectNulls
                                        />
                                    );
                                })}
                            {dataKeys?.map((key) => {
                                return (
                                    <Line
                                        type="monotone"
                                        key={`mean_${key.pairId}`}
                                        dataKey={`mean_${key.pairId}`}
                                        stroke={key.color}
                                        dot={{ fill: key.color }}
                                        strokeWidth={2}
                                        connectNulls
                                    />
                                );
                            })}
                            <Scatter
                                dataKey="total_N"
                                fill="green"
                                shape="triangle"
                            />
                            {bulkSIEnabled && showBulkSI && (
                                <Line
                                    type="monotone"
                                    dataKey="bulk_si"
                                    stroke="black"
                                    connectNulls
                                    dot={false}
                                    strokeWidth={5}
                                />
                            )}
                            {virtualSIEnabled && showVirtualSI && (
                                <>
                                    <Area
                                        type="monotone"
                                        dataKey="virtual_range"
                                        connectNulls
                                        fill="red"
                                        stroke="none"
                                        dot={false}
                                        tooltipType="none"
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="virtual_mean_si"
                                        connectNulls
                                        stroke="red"
                                        dot={false}
                                        strokeWidth={3}
                                    />
                                </>
                            )}
                        </ComposedChart>
                    )}
                </ResponsiveContainer>
            )}
        </div>
    );
}
export default SITrendline;
